'use-client';
import React from 'react';
import {
  formatClassNames,
  getDataAttributes,
} from '@wix/editor-elements-common-utils';
import classNames from 'clsx';
import ResponsiveContainer from '@wix/thunderbolt-elements/src/thunderbolt-core-components/ResponsiveContainer/viewer/ResponsiveContainer';
import repeaterSemanticClassNames from '../../../Repeater.semanticClassNames';
import type { IResponsiveRepeaterProps } from '../../../ResponsiveRepeater.types';
import {
  useSlideshowContext,
  useUpdateSlidesLength,
} from '../../../../Slideshow/SlideshowContext';
import { useIntersectionObserver } from '../../../../../common/hooks/useIntersectionObserver';
import styles from './Slideshow.scss';
import { IFRAME_POST_MESSAGE } from './constants';

const SlideshowSkin: React.FC<IResponsiveRepeaterProps> = props => {
  const {
    id,
    responsiveContainerProps,
    items,
    children,
    className,
    customClassNames = [],
    shouldPauseMediaInNonActiveSlides = false,
  } = props;

  useUpdateSlidesLength(items.length);
  const {
    currentIndex,
    previousIndex,
    onAnimationEnd,
    navigatingForward,
    setIsSlideshowInViewport,
    isButtonFocused,
  } = useSlideshowContext();

  const repeaterRef = React.useRef<HTMLDivElement>(null);
  const itemRefs = React.useRef<Map<string, HTMLDivElement>>(new Map());

  useIntersectionObserver({
    ref: repeaterRef,
    callback: entries => {
      entries.forEach(entry => setIsSlideshowInViewport(entry.isIntersecting));
    },
  });

  React.useEffect(() => {
    if (!shouldPauseMediaInNonActiveSlides) {
      return;
    }
    items.forEach((itemId, index) => {
      const itemElement = itemRefs.current.get(itemId);
      if (!itemElement) {
        return;
      }

      const isCurrent = index === currentIndex;
      const videos = itemElement.querySelectorAll<HTMLVideoElement>('video');
      const iframes = itemElement.querySelectorAll<HTMLIFrameElement>('iframe');

      if (isCurrent) {
        videos.forEach(video => {
          if (video.autoplay) {
            video.play().catch(() => {});
          }
        });
        iframes.forEach(iframe => {
          if (!iframe.closest('[data-autoplay="true"]')) {
            return;
          }
          try {
            Object.values(IFRAME_POST_MESSAGE).forEach(player => {
              iframe.contentWindow?.postMessage(player.play, '*');
            });
          } catch {}
        });
      } else {
        videos.forEach(video => {
          video.pause();
        });
        iframes.forEach(iframe => {
          try {
            Object.values(IFRAME_POST_MESSAGE).forEach(player => {
              iframe.contentWindow?.postMessage(player.pause, '*');
            });
          } catch {}
        });
      }
    });
  }, [currentIndex, items, shouldPauseMediaInNonActiveSlides]);

  const setItemRef = React.useCallback(
    (itemId: string, element: HTMLDivElement | null) => {
      if (element) {
        itemRefs.current.set(itemId, element);
      } else {
        itemRefs.current.delete(itemId);
      }
    },
    [],
  );

  return (
    <div
      id={id}
      {...getDataAttributes(props)}
      className={classNames(
        styles.container,
        className,
        formatClassNames(repeaterSemanticClassNames.root, ...customClassNames),
        {
          [styles.slideToPrev]:
            !navigatingForward && currentIndex !== previousIndex,
          [styles.slideToNext]:
            navigatingForward && currentIndex !== previousIndex,
        },
      )}
      ref={repeaterRef}
      tabIndex={-1}
    >
      <ResponsiveContainer
        {...responsiveContainerProps}
        ariaLive={isButtonFocused ? 'polite' : 'off'}
        tabIndex={-1}
      >
        {() =>
          items.map((itemId, index) => {
            const isCurrent = index === currentIndex;
            const isPrevious = index === previousIndex;

            return (
              <div
                key={itemId}
                ref={el => setItemRef(itemId, el)}
                {...(isCurrent && { onAnimationEnd })}
                className={classNames(styles.item, {
                  [styles.current]: isCurrent,
                  [styles.prev]: isPrevious,
                })}
              >
                {children({
                  parentType: 'Repeater',
                  scopeId: itemId,
                  itemIndex: index,
                })}
              </div>
            );
          })
        }
      </ResponsiveContainer>
    </div>
  );
};

export default SlideshowSkin;
